<template>
  <div
    class="chat-container flex flex-col"
    :style="{ height: chatContainerHeight }"
  >
    <div class="chat-container__gradient"></div>
    <wegow-loader v-if="loading" id="loader" />
    <div
      id="message-container"
      ref="messageContainer"
      :class="{ hidden: loading }"
      class="messages"
      :style="`height: ${height}`"
    >
      <w-chat-message
        v-for="message in messages"
        :key="message.id"
        :message="message"
      />
    </div>
    <div
      class="input flex flex-row items-center border-t gray-border--no-margin mt-2"
    >
      <div class="my-comment flex flex-1 items-center mx-8">
        <wegow-input
          id="search-input"
          ref="momentInput"
          v-model:value="newMessage"
          class="w-full"
          label-for="query"
          :placeholder="$t('wegow.moment.chatMessagePlaceholder')"
          background-input="bg-transparent"
          border-input="border-transparent"
          :disabled="!chat"
          @enter="
            sendMessage(chat, newMessage);
            newMessage = '';
          "
        />
        <div class="w-1/4 flex justify-end">
          <wegow-icon-button
            :src="require('@/assets/images/ic-send.svg')"
            alt="send icon"
            :is-disabled="!newMessage || !chat || !canSendMessage"
            @click="
              sendMessage(chat, newMessage);
              newMessage = '';
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import WegowButton from "@/components/general/forms/WegowButton.vue";
import WegowIconButton from "@/components/general/forms/WegowIconButton.vue";
import WegowInput from "@/components/general/forms/WegowInput.vue";
import WChatMessage from "@/components/general/ui/elements/WChatMessage.vue";

import { useChat } from "@/composables/useChat";
import { useUser } from "@/composables/useUser";
import { onBeforeUnmount, ref, toRefs, watch, computed } from "vue";
import WLoaderComplete from "@/App.vue";
import WegowLoader from "@/components/general/ui/WegowLoader.vue";

const {
  sendChatMessage,
  sendChatVenueId,
  getChatMessages,
  closeWS,
  messages,
  canSendMessage
} = useChat();
const { userDetail } = useUser();

const props = defineProps({
  chat: { type: Object, default: () => {} },
  height: { type: String, default: "420px" },
  theme: { type: String, default: "" } 
});

const { chat } = toRefs(props);
const newMessage = ref("");
const messageContainer = ref(null);
const loading = ref(false);

const chatContainerHeight = computed(() => {
  if (props.theme === "page") {
    return "480px";
  } else if (props.theme === "compact") {
    return "300px";
  }
  return props.height;
});

const sendMessage = async (chat, message) => {
  if (!newMessage.value) return;
  if (props.isVenue) {
    await sendChatVenueId({
      chat: chat.id,
      message,
      action: "sendMessage"
    });
  } else {
    await sendChatMessage({
      chat: chat.id,
      user: userDetail.value.id,
      text: message
    });
  }
};

const scrollToBottom = () => {
  const scrollItem = element => {
    element.scrollTop = element.scrollHeight;
  };
  waitForElementToBecomeVisible(messageContainer.value, scrollItem);
};

const waitForElementToBecomeVisible = (
  element,
  func,
  interval = 10,
  timeout = 10000
) => {
  let elapsedTime = 0;

  const checkVisibility = () => {
    if (!element.classList.contains("hidden")) {
      clearInterval(intervalId);
      func(element);
    } else if (elapsedTime >= timeout) clearInterval(intervalId);
    elapsedTime += interval;
  };
  const intervalId = setInterval(checkVisibility, interval);
};

onBeforeUnmount(() => closeWS());

watch(chat, async (newValue, oldValue) => {
  if (!newValue) return (messages.value = []);
  if (newValue.id === oldValue?.id) return;
  loading.value = true;
  await getChatMessages({ chat: newValue.id });
  loading.value = false;
});

watch(messages, () => scrollToBottom(), { deep: true, flush: "post" });
</script>

<style lang="scss" scoped>
.chat-container {
  position: relative;
  justify-content: space-between;
  max-height: 100%;
  &__gradient {
    width: 100%;
    height: 100%;
    z-index: 1;
    position: absolute;
    top: 0;
    background: linear-gradient(
      180deg,
      rgb(28, 29, 41) 0%,
      rgba(28, 29, 41, 0) 70%
    );
    pointer-events: none;
  }
  #loader,
  #message-container {
    @apply overflow-auto flex-auto hidden-scrollbars;
  }
}
.input {
  height: 46.5px;
}
</style>
